import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, Route, Routes, useNavigate, useLocation, useParams } from 'react-router-dom';
import cx from 'classnames';
import { toast } from 'react-toastify';
import queryString from 'query-string';
import filter from 'ramda/src/filter';
import find from 'ramda/src/find';
import findIndex from 'ramda/src/findIndex';
import includes from 'ramda/src/includes';
import isNil from 'ramda/src/isNil';
import prop from 'ramda/src/prop';
import propEq from 'ramda/src/propEq';
import sortBy from 'ramda/src/sortBy';
import toLower from 'ramda/src/toLower';
import compose from 'ramda/src/compose';
import Avatar from '@components/@core/Avatar';
import * as Animations from '@components/@core/Animations';
import Button from '@components/@core/AnimatedButton';
import Dropdown from '@components/@core/Dropdown';
import EditDropdown from '@components/EditDropdown';
import Icon from '@components/@core/Icon';
import NotFound from '@components/NotFound';
import AppActions from '~/actions/app';
import CommunityActions from '~/actions/community';
import CompanyActions from '~/actions/company';
import { getCompanyName } from '~/services/util';
import { UserRoles } from '~/services/config';
import useOutsideClick from '~/services/useOutsideClick';
import About from './About';
import Products from './Products';
import Library from './Library';
import Blog from './Blog';
import Connections from './Connections';
import HumanCapital from './HumanCapital';
import AccountAdmin from './AccountAdmin';
import ProductsAdmin from './ProductsAdmin';
import ConnectionsAdmin from './ConnectionsAdmin';
import BlogAdmin from './BlogAdmin';
import Reports from './Reports';
import VendorRatingsReports from './Reports/VendorRatings';
import HCMAdmin from './HCMAdmin';

const CROUTES = [
    { id: 0, name: 'about', label: 'About', report: 'about' },
    { id: 1, name: 'all', label: 'Products', icon: 'fas fa-star', report: 'products' },
    { id: 2, name: 'library', label: 'Library', icon: 'fas fa-university', report: 'library' },
    { id: 4, name: 'blog', label: 'Blog', icon: 'fas fa-comment-alt-edit', report: 'blog' },
    { id: 5, name: 'connections', label: 'Connections', icon: 'fas fa-users', report: 'connections' },
    {
        id: 6,
        name: 'human-capital',
        label: 'HCM',
        icon: 'fas fa-tasks',
        report: 'human-capital',
    },
];
const COPTIONS = [
    { id: 7, name: 'account-admin', label: 'Account Admin' },
    { id: 8, name: 'products-admin', label: 'Products Admin' },
    { id: 9, name: 'blog-admin', label: 'Blog Admin' },
    { id: 10, name: 'connections-admin', label: 'Connections Admin' },
    { id: 11, name: 'hcm-admin', label: 'HCM Admin' },
    { id: 12, name: 'reports', label: 'Reports' },
];
const GEAR_OPTIONS = COPTIONS.map((e) => e.name);

const Profile = ({ businessId }) => {
    const navigate = useNavigate();
    const param = useParams();
    const location = useLocation();
    const dispatch = useDispatch();
    const profileHeader = useRef();
    const gearDropdown = useRef();

    const search = queryString.parse(location.search);

    const token = useSelector((state) => state.app.token);
    const role = useSelector((state) => state.app.primary_role_id);
    const user = useSelector((state) => state.app.user);
    const parents = useSelector((state) => state.app.businesses);
    const businesies = useSelector((state) => state.company.business);
    const employee = useSelector((state) => state.app.selectedEmployee);

    const isHCMSuperAdmin = user?.primary_role_id === 1;
    const isHCMAdmin = user?.primary_role_id <= 2;
    const isCommunitySuperAdmin = !!user?.community_user?.super_admin;
    const [business, path] = useMemo(() => {
        let cbusiness, cpath;
        if (param.entity && param.entity !== 'business') {
            cpath = `/${param.entity}`;
        } else {
            cpath = '/business';
        }
        cbusiness = find(propEq('id', Number(search?.id) || businessId), businesies);
        return [cbusiness, cpath];
    }, [param.entity, search?.id, businesies]);
    const profile = { ...business?.hcms?.data?.profile, ...business?.data?.profile };
    const communityUser = user?.community_user;
    const communityUserId = communityUser?.id;
    const isVRCompany = !!business?.vrs?.id;
    const isSponsored = !isVRCompany || (business?.vrs?.data?.sponsored_level && business?.vrs?.data?.sponsored_level !== 'wiki');
    const connections = user?.community_user?.connections;
    const [isBlogAdmin, isVRAdmin, isBusinessAdmin, isProductAdmin, isAccountAdmin] = useMemo(() => {
        if (user && business) {
            const blogId = business.vrs?.blog?.id ?? 0;
            const vrId = business.vrs?.id ?? 0;
            const businessId = business?.id ?? 0;
            const adminBlogs = (user.community_user?.blogs ?? []).map((e) => e.blog);
            const isBlog = findIndex(propEq('id', blogId), adminBlogs) > -1;
            const adminVRs = user.community_user?.companies ?? [];
            const isVR = findIndex(propEq('id', vrId), adminVRs) > -1;
            const adminBusiness = user.businesses ?? [];
            const isBusiness = findIndex(propEq('id', businessId), adminBusiness) > -1;
            const isProd = isCommunitySuperAdmin || isBusiness || isVR;
            const isAccount = isHCMSuperAdmin || isHCMAdmin || isCommunitySuperAdmin || isBusiness || isBlog || isVR;
            return [isBlog, isVR, isBusiness, isProd, isAccount];
        }
        const isAccount = isHCMSuperAdmin || isHCMAdmin || isCommunitySuperAdmin;
        return [false, false, false, isCommunitySuperAdmin, isAccount];
    }, [user, business]);

    const [defaultId, setDefaultId] = useState(0);
    const [editable, setEditable] = useState(false);
    const [opened, setOpened] = useState(false);
    const [avatar, setAvatar] = useState(business?.vrs?.avatar || profile?.avatar || '');
    const [cover, setCover] = useState(business?.vrs?.cover_url || profile?.cover);
    let defaultCover = isVRCompany && !isSponsored ? 'https://s3.amazonaws.com/drivingsales.com-assets/static/images/cover.png' : cover;

    const facebook = profile?.facebook;
    const twitter = profile?.twitter;
    const linkedin = profile?.linked_in;
    const youtube = profile?.youtube;

    const [ROUTES, OPTIONS] = useMemo(() => {
        let _ROUTES = CROUTES;
        let _OPTIONS = COPTIONS;

        if (!isVRCompany || business?.data?.licences?.vendor_ratings === 'disable' || (isNil(business?.data?.licences?.vendor_ratings) && business?.data?.licences?.vendor_rating === false)) {
            _ROUTES = filter((x) => x.id !== 1, _ROUTES);
            _OPTIONS = filter((x) => x.name !== 'products-admin', _OPTIONS);
        }
        if (business?.data?.licences?.community !== 'enable' && business?.data?.licences?.community !== true && !business?.hcms) {
            _ROUTES = filter((x) => x.id !== 6, _ROUTES);
        }
        if (!business?.hcms && !isHCMSuperAdmin) {
            _ROUTES = filter((x) => x.id !== 2 && x.id !== 3, _ROUTES);
        }
        if (
            (!business?.vrs?.blog?.id && !isCommunitySuperAdmin) ||
            business?.data?.licences?.blog === 'disable' ||
            (isNil(business?.data?.licences?.blog) && business?.data?.licences?.blogs === false)
        ) {
            _ROUTES = filter((x) => x.id !== 4, _ROUTES);
        }
        if ((!isVRCompany && !isCommunitySuperAdmin) || business?.data?.licences?.hcm === 'disable' || business?.data?.licences?.hcm === false) {
            _ROUTES = filter((x) => x.id !== 5, _ROUTES);
        }
        if (!isBlogAdmin) {
            _OPTIONS = filter((x) => x.name !== 'blog-admin', _OPTIONS);
        }
        if (!isBlogAdmin && !isCommunitySuperAdmin) {
            _OPTIONS = filter((x) => x.name !== 'reports', _OPTIONS);
        }
        if (!isProductAdmin) {
            _OPTIONS = filter((x) => x.name !== 'products-admin', _OPTIONS);
        }
        if (!isCommunitySuperAdmin && !isBusinessAdmin) {
            _OPTIONS = filter((x) => x.name !== 'connections-admin' && x.name !== 'reports', _OPTIONS);
        }
        if (!business?.hcms || !isHCMAdmin) {
            _OPTIONS = filter((x) => x.name !== 'hcm-admin', _OPTIONS);
        }
        if (!isAccountAdmin) {
            _OPTIONS = filter((x) => x.name !== 'account-admin', _OPTIONS);
        }
        return [_ROUTES, _OPTIONS];
    }, [isHCMAdmin, isHCMSuperAdmin, isBlogAdmin, isBusinessAdmin, isCommunitySuperAdmin, isVRAdmin, isVRCompany, isSponsored, business]);

    useEffect(() => {
        dispatch(AppActions.getentityconnectionsRequest(false));
        dispatch(CompanyActions.getblogcategoriesRequest(false));
        if (token && business?.id) {
            dispatch(CompanyActions.getbusinessblogRequest({ businessId: business.id }, false));
        }
        if (search.blog_id || business?.vrs?.blog?.id) {
            dispatch(
                CommunityActions.getcomblogpostsRequest({
                    blogId: search.blog_id || business?.vrs?.blog?.id,
                    perPage: 10,
                })
            );
        }
    }, []);

    useEffect(() => {
        profileHeader?.current && location.pathname !== '/business/products-admin' && location.pathname !== '/business/account-admin' && profileHeader?.current.scrollIntoView({ behavior: 'smooth' });
        if (editable) setEditable(false);
        if (business?.vrs?.avatar && avatar !== business.vrs.avatar) setAvatar(business.vrs.avatar);
        if (business?.vrs?.cover_url && business?.vrs?.cover_url !== cover) setCover(business?.vrs?.cover_url);
        if (!business) {
            const payload = { id: Number(search?.id) || businessId };
            dispatch(CompanyActions.getbusinessRequest(payload));
        }
    }, [business]);

    useOutsideClick(gearDropdown, () => {
        setOpened(false);
    });

    const handleDropdown = (e) => {
        if (!isBlogAdmin && !isVRAdmin && !isBusinessAdmin && !isProductAdmin && !isAccountAdmin && role > UserRoles.ADMIN) {
            toast.error('You must be the company admin to access that feature.', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            return;
        }
        switch (e) {
            case 'edit':
                setEditable(true);
                break;
            case 'account':
                break;
            default:
                break;
        }
    };

    const handleClickEditButtons = (type) => (e) => {
        if (type !== 'social') {
            if (!isSponsored && !business?.hcms) {
                toast.error('Please upgrade your company to a Vendor Ratings sponsor or subscribe to HCM to enable this feature.', {
                    position: toast.POSITION.TOP_RIGHT,
                    pauseOnFocusLoss: false,
                    hideProgressBar: true,
                });
                return;
            }
        }
        switch (type) {
            case 'featured':
                dispatch(
                    AppActions.modalRequest({
                        type: 'Upload Background Image',
                        data: {
                            before: {
                                entity: business?.vrs?.entity?.id,
                                title: 'Cover Image will be converted into 1145 x 220',
                            },
                        },
                        callBack: (e) => setCover(e),
                    })
                );
                break;

            case 'avatar':
                dispatch(
                    AppActions.modalRequest({
                        type: 'Upload Vendor Avatar',
                        data: {
                            before: {
                                entity: business?.vrs?.entity?.id,
                                title: 'Avatar image will be converted into 175 x 175',
                            },
                        },
                        callBack: (e) => setAvatar(e),
                    })
                );
                break;

            case 'social':
                dispatch(
                    AppActions.modalRequest({
                        type: 'Link Social Networks',
                        data: { before: { company: business } },
                    })
                );
                break;

            default:
                break;
        }
    };

    const handleTabs = (e) => {
        navigate(business?.vrs?.entity?.slug ? `/${business.vrs.entity.slug}/${e[0].name}` : `${path}/${e[0].name}`);
        setDefaultId(e[0].id);
    };

    const handleProduct = (e) => {
        navigate(business?.vrs?.entity?.slug ? `/${business.vrs.entity.slug}/${e}` : `${path}/${e}`);
    };

    const handleNavigation = (tab) => {
        if (tab) navigate(business?.vrs?.entity?.slug ? `/${business.vrs.entity.slug}/${tab}` : `${path}/${tab}`);
    };

    const handleChangeCompany = (e) => {
        if (e[0].business_id) {
            const payload = { id: e[0].business_id };
            const route = e[0]?.entity?.slug ? { pathname: `/${e[0].entity.slug}/about` } : { pathname: '/business/about', search: `?id=${e[0].business_id}` };
            dispatch(CompanyActions.getbusinessRequest(payload, route));
            dispatch(AppActions.globalfilterRequest([e[0].id], employee));
        }
    };

    const handleConnect = () => {
        const payload = { user_id: communityUser?.id, connect_company_id: business?.vrs?.id };
        const after = { type: 'GETENTITYCONNECTIONS_REQUEST' };
        dispatch(CommunityActions.postcommunityconnectRequest(payload, null, after, null));
    };

    const selfCompany = findIndex(propEq('business_id', business?.id), communityUser?.companies || []) > -1;
    const connected = findIndex(propEq('business_id', business?.id), connections?.active_company_connections || []) > -1;
    const pendingConnects = [...(connections?.pending_connections_received || []), ...(connections?.pending_connections_sent || [])];
    const pendingStatus = findIndex((x) => x.connector?.content_id === business?.vrs?.id, pendingConnects) > -1;
    return (
        <div className="cprofile" ref={profileHeader}>
            {token && (
                <Dropdown
                    align="left"
                    getId={(e) => e.business_id}
                    defaultIds={[business?.vrs?.business_id ?? business?.id ?? 0]}
                    data={user?.companies ? sortBy(compose(toLower, prop('name')), user.companies) : []}
                    getValue={(e) => e.name}
                    returnBy="data"
                    onChange={handleChangeCompany}
                />
            )}
            <div className="cprofile-header">
                <div className="top">
                    <EditDropdown className="edit" iconColor="#fff" options={['Edit']} onChange={handleDropdown} />
                    <div
                        className="background"
                        style={{
                            backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.4), transparent),url(${isSponsored ? cover : defaultCover})`,
                        }}
                    >
                        <div className="empty" />
                    </div>
                    <div className="info">
                        <div className="logo">
                            <Avatar className={cx(editable && 'edit-logo')} isSponsored={isSponsored} name={business?.name} url={avatar} size="large" />
                            <div className="info-title">
                                <h3 className="dsl-b28 mb-4">{getCompanyName(business)}</h3>
                                {business?.stats?.follow && <h6 className="dsl-b14">{`Followed by: ${business?.stats?.follow}`}</h6>}
                            </div>
                        </div>
                        <div className="info-social pt-4">
                            {pendingStatus ? (
                                <p className="dsl-w14">Connection request has been sent...</p>
                            ) : (
                                <>{!selfCompany && !connected && <Button className="connect-button" type="medium" name="Connect" onClick={handleConnect} />}</>
                            )}
                            <div className="info-social-buttons">
                                {facebook && (
                                    <Button className="social-link-button" type="link" onClick={() => window.open(facebook, '_blank')}>
                                        <Icon name="fab fa-facebook-f" size={14} color="white" />
                                    </Button>
                                )}
                                {twitter && (
                                    <Button className="social-link-button" type="link" onClick={() => window.open(twitter, '_blank')}>
                                        <Icon name="fab fa-twitter ms-3" size={14} color="white" />
                                    </Button>
                                )}
                                {linkedin && (
                                    <Button className="social-link-button" type="link" onClick={() => window.open(linkedin, '_blank')}>
                                        <Icon name="fab fa-linkedin-in ms-3" size={14} color="white" />
                                    </Button>
                                )}
                                {youtube && (
                                    <Button className="social-link-button" type="link" onClick={() => window.open(youtube, '_blank')}>
                                        <Icon name="fab fa-youtube ms-3" size={14} color="white" />
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                    {editable && (
                        <div className="edit-header">
                            <Button className="edit-featured-image" type="link" onClick={handleClickEditButtons('featured')}>
                                <Icon name="fas fa-pen" size={14} color="#343f4b" />
                            </Button>
                            <div className="info">
                                <div className="position-relative" style={{ backgroundImage: `url(${avatar})` }}>
                                    <Button className="edit-company-avatar" type="link" onClick={handleClickEditButtons('avatar')}>
                                        <Icon name="fas fa-pen" size={14} color="#343f4b" />
                                    </Button>
                                </div>
                                <div className="info-social align-self-end">
                                    <div className="info-social-buttons">
                                        <Button className="edit-social-links" type="link" onClick={handleClickEditButtons('social')}>
                                            <Icon name="fas fa-pen" size={14} color="#343f4b" />
                                        </Button>
                                        {facebook && (
                                            <Button className="social-link-button" type="link">
                                                <Icon name="fab fa-facebook-f" size={14} color="#343f4b" />
                                            </Button>
                                        )}
                                        {twitter && (
                                            <Button className="social-link-button" type="link">
                                                <Icon name="fab fa-twitter ms-3" size={14} color="#343f4b" />
                                            </Button>
                                        )}
                                        {linkedin && (
                                            <Button className="social-link-button" type="link">
                                                <Icon name="fab fa-linkedin-in ms-3" size={14} color="#343f4b" />
                                            </Button>
                                        )}
                                        {youtube && (
                                            <Button className="social-link-button" type="link">
                                                <Icon name="fab fa-youtube ms-3" size={14} color="#343f4b" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="bottom">
                    {ROUTES.map((item, index) => (
                        <NavLink
                            key={`navlink-${index}`}
                            className="section dsl-d14"
                            active="active"
                            to={business?.vrs?.entity?.slug ? `/${business.vrs.entity.slug}/${item.name}` : `${path}/${item.name}?id=${business?.id}`}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                    {OPTIONS.length > 0 && (
                        <div className={cx('section', includes(param.tab, GEAR_OPTIONS) && 'active')}>
                            <div className="position-relative" ref={gearDropdown}>
                                <div className="px-3 cursor-pointer" onClick={() => setOpened(!opened)}>
                                    <Icon name="fas fa-gear" color="white" size={14} />
                                </div>
                                <Animations.Popup className="popup box-shadow" enter={10} exit={0} opened={opened}>
                                    {OPTIONS.map((item, index) => (
                                        <NavLink
                                            className="dsl-d14"
                                            active="active"
                                            key={`option${index}`}
                                            to={business?.vrs?.entity?.slug ? `/${business.vrs.entity.slug}/${item.name}` : `${path}/${item.name}?id=${business?.id}`}
                                            onClick={() => setOpened(false)}
                                        >
                                            {item.label}
                                        </NavLink>
                                    ))}
                                </Animations.Popup>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Dropdown className="mobile-screen" data={ROUTES.concat(OPTIONS)} defaultIds={[defaultId]} returnBy="data" getValue={(e) => e.label} onChange={handleTabs} />
            <Routes>
                <Route
                    path="about"
                    element={
                        <About
                            business={business}
                            editable={editable}
                            avatar={avatar}
                            cover={cover}
                            updated={cover !== business?.vrs?.cover_url || avatar !== business?.vrs?.avatar}
                            onCancel={() => setEditable(false)}
                        />
                    }
                />
                {/*
        <Route exact path={['/:entity/blog', '/:entity/blog/:post']}>
          <Blog isAdmin={isBlogAdmin} data={business} />
        </Route>
            */}
                <Route path="reports/vendor-ratings" element={<VendorRatingsReports data={business} />} />
                <Route path="library" element={<Library />} />
                <Route path="human-capital" element={<HumanCapital businessId={business?.id} data={business?.hcms} />} />
                <Route path="connections" element={<Connections business={business} />} />

                {isAccountAdmin && <Route path="account-admin" element={<AccountAdmin data={business} />} />}
                {isProductAdmin && <Route path="products-admin" element={<ProductsAdmin data={business} onNavigation={handleNavigation} />} />}
                {(isVRCompany || isCommunitySuperAdmin) && <Route path="connections-admin" element={<ConnectionsAdmin business={business} />} />}
                {isBlogAdmin && <Route path="blog-admin" element={<BlogAdmin companyId={business?.id} userId={communityUserId} data={business} onBlogDetail={() => {}} />} />}
                {(isBlogAdmin || isCommunitySuperAdmin) && <Route path="reports" element={<Reports id={business?.id} rootRoute={`${path}`} routesList={ROUTES} />} />}
                {isVRCompany && <Route path="products" element={<VendorRatingsReports data={business?.vrs} />} />}
                {isHCMAdmin && <Route path="hcm-admin" element={<HCMAdmin data={business?.hcms} />} />}
                <Route path={param.product} element={<Products data={business?.vrs} isAdmin={isProductAdmin} slug={param.product} onProduct={handleProduct} />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
        </div>
    );
};

export default Profile;
